import { DateInputLocalized, InputNumberLocalized } from '@components'

import { Divider, FormItem, FormItemsGroup, Space, Text } from '@billy/design-system'
import { ReactElement, ReactNode, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BillAccountSelector } from '@views/bills/elements/BillReconciliationModal/elements/BillAccountSelector'

import { useSegment } from '../../../../hooks'
import {
  useManualPayment,
  useMultiplePayableSubjects,
  usePaymentModal,
} from '../../../paymentReconciliationModal/contexts'
import { CrossCurrencyPaymentAmount } from '../../../paymentReconciliationModal/elements/BankLinesModalBody/elements/CrossCurrencyPaymentAmount'
import { LinkToBankLines } from '../../../paymentReconciliationModal/elements/MultipleBillsModalFooter/elements/LinkToBankLines'
import { SubjectSummary } from '../../../paymentReconciliationModal/elements/SubjectSummary'
import { ManualPaymentInfo } from './elements/ManualPaymentInfo'

export const ManualPaymentModalBody = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { paymentDate, setPaymentDate, isCrossCurrency, selectedAccount } = usePaymentModal()
  const { initialSubject, isCashBasis } = useMultiplePayableSubjects()
  const { hasManualPaymentDifference, manualPaymentAmount, manualPaymentDifference, setManualPaymentAmount } =
    useManualPayment()

  const accountCurrencyId = selectedAccount?.currencyId

  const handlePaymentAmountChange = (amount?: number) => {
    setManualPaymentAmount(amount || 0)
  }

  useEffect(() => {
    track('xxx - expenses - Manual payment modal opened')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dateInputError = useMemo((): ReactNode => {
    return (
      paymentDate === null && (
        <Text colorVariant="danger">{t('bill.payment_reconcile.prepayment.chose_payment_date')}</Text>
      )
    )
  }, [paymentDate, t])

  const amountInputError = useMemo((): ReactNode => {
    return (
      !manualPaymentAmount && (
        <Text colorVariant="danger">{t('bill.payment_reconcile.prepayment.choose_payment_amount')}</Text>
      )
    )
  }, [manualPaymentAmount, t])

  return (
    <>
      <SubjectSummary />
      <Space />
      <Divider />
      <Space />
      {isCrossCurrency && (
        <CrossCurrencyPaymentAmount
          convertedCurrencyId={accountCurrencyId}
          subjectCurrencyId={initialSubject?.currencyId}
        />
      )}
      <Space />
      <BillAccountSelector />
      <Space />
      <FormItemsGroup itemsInRow={4}>
        <FormItem label={t('bill.payment_reconcile.payment_date')} error={dateInputError}>
          <DateInputLocalized name="paymentDate" onChange={setPaymentDate} value={paymentDate} />
        </FormItem>

        <FormItem itemColumnEnd={5} itemColumnStart={2} label={t('amount')} error={amountInputError}>
          <InputNumberLocalized
            name="manualPaymentAmount"
            disabled={isCashBasis}
            value={manualPaymentAmount}
            defaultValue={manualPaymentAmount}
            onChange={handlePaymentAmountChange}
            suffix={accountCurrencyId && <Text colorVariant="secondary">{accountCurrencyId}</Text>}
          />
        </FormItem>
      </FormItemsGroup>
      <ManualPaymentInfo
        isCrossCurrency={isCrossCurrency}
        hasManualPaymentDifference={hasManualPaymentDifference}
        manualPaymentDifference={manualPaymentDifference}
      />
      {!isCashBasis && <LinkToBankLines />}
    </>
  )
}
